import { useEffect, useRef, useState } from 'react'
import { ChevronLeft } from 'lucide-react'
import useStore from '@/stores/useStore'
import { CENSUS_MODES } from '@/lib/demoEntitlements'
import { LOAD_CENSUS_EVENT } from '@/lib/saveLoadEvents'
import OnboardingStepPeople from '@/components/onboarding/OnboardingStepPeople'
import OnboardingStepExistingClient from '@/components/onboarding/OnboardingStepExistingClient'
import OnboardingStepPlan from '@/components/onboarding/OnboardingStepPlan'
import OnboardingStepResults from '@/components/onboarding/OnboardingStepResults'

// First-run guided flow. Rendered by CalculatorWorkspace in place of the
// workspace panes (which stay mounted but hidden, so census uploads and the
// estimator keep working through their existing dialogs and events).
export default function OnboardingFlow({
  onComplete,
  onStartEstimate,
  forceOnboarding = false,
  holdAtChoices = false,
  restartKey = 0,
  resumeAtResults = false,
}) {
  const [step, setStep] = useState(0)
  const [selectedPath, setSelectedPath] = useState(null)
  const [stayAtChoices, setStayAtChoices] = useState(false)
  const importBaselineRef = useRef(null)
  const employees = useStore((state) => state.employees)
  const censusMode = useStore((state) => state.censusMode)
  const workspaceMode = useStore((state) => state.workspaceMode)
  const stashedActual = useStore((state) => state.stashedActual)
  const metrics = useStore((state) => state.analysis.planA.metrics)
  const exitEstimateMode = useStore((state) => state.exitEstimateMode)
  const deleteAllEmployees = useStore((state) => state.deleteAllEmployees)
  const loadExampleData = useStore((state) => state.loadExampleData)
  const savedEmployees = workspaceMode === 'estimate'
    ? (stashedActual?.employees || [])
    : employees
  const savedCensusMode = workspaceMode === 'estimate'
    ? stashedActual?.censusMode
    : censusMode
  const hasSavedCensus = savedEmployees.length > 0 && savedCensusMode !== CENSUS_MODES.EXAMPLE

  // Returning to the starting choices can happen from any guide step.
  useEffect(() => {
    if (resumeAtResults) {
      setStep(2)
      setSelectedPath(null)
      setStayAtChoices(false)
      importBaselineRef.current = null
      return
    }
    setStep(0)
    setSelectedPath(null)
    setStayAtChoices(true)
    importBaselineRef.current = null
  }, [restartKey, resumeAtResults])

  // A results summary is only a destination when there is a calculated result
  // to summarize. This also protects browser-history restores after the census
  // has been cleared in the workspace.
  useEffect(() => {
    if (step === 2 && !metrics) onComplete('people')
  }, [metrics, onComplete, step])

  // Step 1 completes when data arrives through any route – CSV upload, Import
  // Studio commit, or an estimate session. Those flows run in portaled dialogs
  // over this screen, so watch the store rather than wiring every callback.
  useEffect(() => {
    if (step !== 0) return
    if (resumeAtResults) return
    if (selectedPath === 'estimate') {
      if (workspaceMode === 'estimate') setStep(1)
      return
    }
    if (selectedPath === 'existing') {
      if (importBaselineRef.current && employees !== importBaselineRef.current) setStep(1)
      return
    }
    if (forceOnboarding || holdAtChoices || stayAtChoices) return
    if (
      employees.length > 0 &&
      (workspaceMode === 'estimate' || censusMode === CENSUS_MODES.CUSTOM)
    ) {
      setStep(1)
    }
  }, [censusMode, employees, forceOnboarding, holdAtChoices, resumeAtResults, selectedPath, stayAtChoices, step, workspaceMode])

  const startExistingImport = (files = null) => {
    if (workspaceMode === 'estimate') exitEstimateMode()
    importBaselineRef.current = useStore.getState().employees
    window.dispatchEvent(new CustomEvent(LOAD_CENSUS_EVENT, {
      detail: files?.length ? { files } : null,
    }))
  }

  const returnToChoices = () => {
    setStep(0)
    setSelectedPath(null)
    setStayAtChoices(true)
    importBaselineRef.current = null
  }

  const startDemo = () => {
    // Demo is a clean, deterministic starting path. If the user arrived from
    // Prospect Mode, restore the stashed workspace first so none of the
    // synthetic estimate state can leak into the built-in sample.
    if (workspaceMode === 'estimate') exitEstimateMode()
    loadExampleData()
    setStayAtChoices(false)
    setSelectedPath('demo')
    setStep(1)
  }

  const startEmpty = () => {
    if (workspaceMode === 'estimate') exitEstimateMode()
    deleteAllEmployees()
    onComplete('people')
  }

  return (
    <div className="flex flex-1 min-h-0 flex-col overflow-y-auto bg-background">
      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-8 px-4 py-10 sm:px-6">
        <div className="flex-1">
          {step === 0 && selectedPath === 'existing' ? (
            <OnboardingStepExistingClient
              onBack={returnToChoices}
              onImport={startExistingImport}
            />
          ) : step === 0 && (
            <OnboardingStepPeople
              onStartEstimate={() => {
                setStayAtChoices(false)
                setSelectedPath('estimate')
                onStartEstimate()
              }}
              onUseExample={startDemo}
              onStartEmpty={startEmpty}
              onUseExistingClient={() => {
                setStayAtChoices(false)
                setSelectedPath('existing')
              }}
              hasSavedCensus={hasSavedCensus}
            />
          )}
          {step === 1 && <OnboardingStepPlan onNext={() => setStep(2)} />}
          {step === 2 && metrics && (
            <OnboardingStepResults
              onShowCensus={() => onComplete('people')}
              onShowResults={() => onComplete('results')}
              onChooseAnother={returnToChoices}
            />
          )}
        </div>

        {step < 2 && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            {step > 0 ? (
              <button
                type="button"
                onClick={returnToChoices}
                className="flex items-center gap-1 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
                Back to Choices
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={onComplete}
              className="underline underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Open Calculator
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
